import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { parseFlexibleDate } from '../../lib/dateInput';
import { DatePopover } from '../ui/DatePopover';

/** Simple single-line text editor that commits on blur / Enter and cancels on Esc. */
export function InlineText({
  value,
  onCommit,
  onCancel,
  className,
  placeholder,
}: {
  value: string;
  onCommit: (next: string) => void;
  onCancel: () => void;
  className?: string;
  placeholder?: string;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    ref.current?.select();
  }, []);
  return (
    <input
      ref={ref}
      defaultValue={value}
      placeholder={placeholder}
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => onCommit(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
        e.stopPropagation();
      }}
      className={cn(
        'w-full rounded border border-sky-300 bg-white px-1.5 py-0.5 text-sm outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-200',
        className,
      )}
    />
  );
}

/**
 * Hybrid date editor: a `<input type="text">` accepts typed and pasted
 * values (any format `parseFlexibleDate` understands — `YYYY-MM-DD`,
 * `YYYY/MM/DD`, `YYYY年M月D日`, etc.), plus a calendar icon that pops a
 * custom Asana-style {@link DatePopover}.
 *
 * Why custom not native: the native `<input type="date">` overlay sits
 * outside the React tree; clicking its month-nav arrows blurs the
 * underlying text input, which collapses our editor. The popover
 * lives inside the same React tree so its clicks count as "inside"
 * for outside-click dismissal.
 *
 * Dismissal:
 *   - Outside-click: settle (commit if text parses, else cancel).
 *   - Esc: cancel, no commit.
 *   - Enter: settle from current text.
 *   - Picker click: pick → commit immediately.
 */
export function InlineDate({
  value,
  onCommit,
  onCancel,
  className,
}: {
  /** `YYYY-MM-DD` string or empty. */
  value: string;
  onCommit: (next: string) => void;
  onCancel: () => void;
  className?: string;
}) {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const textInputRef = useRef<HTMLInputElement | null>(null);
  const [text, setText] = useState(value);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Latest text mirrored into a ref so the outside-click handler can
  // settle without re-installing on every keystroke.
  const textRef = useRef(text);
  textRef.current = text;
  const settled = useRef(false);

  useEffect(() => {
    textInputRef.current?.focus();
    textInputRef.current?.select();
  }, []);

  const settle = useCallback(
    (commit: boolean) => {
      if (settled.current) return;
      settled.current = true;
      if (!commit) {
        onCancel();
        return;
      }
      const parsed = parseFlexibleDate(textRef.current);
      // Garbage on commit reverts — never persist a half-typed string.
      if (parsed === null) onCancel();
      else onCommit(parsed);
    },
    [onCommit, onCancel],
  );

  // Outside-click closes the editor, considering BOTH the trigger and
  // the portal'd popover as "inside". Defer attachment for one tick so
  // the click that opened us doesn't immediately close us.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      settle(true);
    };
    const tid = window.setTimeout(() => {
      document.addEventListener('mousedown', onDown);
    }, 0);
    return () => {
      window.clearTimeout(tid);
      document.removeEventListener('mousedown', onDown);
    };
  }, [settle]);

  // Esc cancels even when the popover has focus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        settle(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [settle]);

  return (
    <>
      <div
        ref={triggerRef}
        className={cn(
          'flex w-full items-center gap-0.5 rounded border border-sky-300 bg-white pr-0.5 focus-within:border-sky-500 focus-within:ring-1 focus-within:ring-sky-200',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={textInputRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          value={text}
          placeholder="YYYY-MM-DD"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              settle(true);
            }
            e.stopPropagation();
          }}
          className="w-full min-w-0 bg-transparent px-1 py-0.5 text-xs outline-none"
        />
        <button
          type="button"
          tabIndex={-1}
          // mousedown (not click) + preventDefault so the text input
          // doesn't blur and re-render with stale state mid-toggle.
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setPickerOpen((v) => !v);
          }}
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-sky-100 hover:text-sky-700"
          aria-label="カレンダーから選択"
          title="カレンダーから選択"
        >
          <Calendar className="h-3 w-3" />
        </button>
      </div>
      {pickerOpen ? (
        <DatePopover
          selected={parseFlexibleDate(text) || value || null}
          anchorEl={triggerRef.current}
          popoverRef={popoverRef}
          onPick={(picked) => {
            settled.current = true;
            setText(picked);
            onCommit(picked);
          }}
          onClear={() => {
            settled.current = true;
            setText('');
            onCommit('');
          }}
        />
      ) : null}
    </>
  );
}

export interface ChoiceOption {
  /** Raw value persisted to Firestore. Empty string = "unset". */
  value: string;
  /** Display label. */
  label: string;
  /** Tailwind classes for the colored badge in the popover and the cell. */
  badgeClasses: string;
}

/**
 * Color-aware dropdown rendered as a portal-anchored popover. Closes on
 * outside click / Escape. A "(未設定)" option at the top clears the value.
 *
 * Anchored relative to `anchorRect` (a DOMRect from the cell) so the
 * popover escapes any parent `overflow-hidden` clipping. Falls back to
 * body if window is too narrow.
 */
export function InlineChoice({
  options,
  value,
  anchorRect,
  onCommit,
  onCancel,
  allowClear = true,
}: {
  options: readonly ChoiceOption[];
  value: string;
  anchorRect: DOMRect;
  onCommit: (next: string) => void;
  onCancel: () => void;
  allowClear?: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    top: anchorRect.bottom + 2,
    left: anchorRect.left,
    minWidth: Math.max(160, anchorRect.width),
    zIndex: 60,
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // If the popover would overflow the viewport right edge, align to the
    // cell's right instead of left.
    if (rect.right > window.innerWidth - 4) {
      setStyle((s) => ({
        ...s,
        left: Math.max(4, anchorRect.right - rect.width),
      }));
    }
    // If it would overflow below, flip above.
    if (rect.bottom > window.innerHeight - 4) {
      setStyle((s) => ({
        ...s,
        top: Math.max(4, anchorRect.top - rect.height - 2),
      }));
    }
  }, [anchorRect]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onCancel();
    };
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onClick);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onCancel]);

  const body = (
    <div
      ref={ref}
      style={style}
      onClick={(e) => e.stopPropagation()}
      className="max-h-72 overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
      role="listbox"
    >
      {allowClear ? (
        <button
          type="button"
          onClick={() => onCommit('')}
          className={cn(
            'flex w-full items-center gap-2 px-2 py-1 text-left text-xs text-slate-500 hover:bg-slate-50',
            value === '' ? 'bg-slate-100' : '',
          )}
        >
          <span className="inline-flex h-3.5 w-3.5 items-center justify-center">
            {value === '' ? <Check className="h-3 w-3 text-slate-500" /> : null}
          </span>
          <span>(未設定)</span>
        </button>
      ) : null}
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value || '__empty'}
            type="button"
            onClick={() => onCommit(opt.value)}
            className={cn(
              'flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-slate-50',
              selected ? 'bg-slate-100' : '',
            )}
            role="option"
            aria-selected={selected}
          >
            <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
              {selected ? <Check className="h-3 w-3 text-slate-700" /> : null}
            </span>
            <span
              className={cn(
                'inline-block min-w-0 flex-1 truncate rounded px-1.5 py-0.5 text-[11px] font-medium',
                opt.badgeClasses,
              )}
              title={opt.label}
            >
              {opt.label}
            </span>
          </button>
        );
      })}
      {options.length === 0 ? (
        <div className="px-2 py-2 text-center text-xs text-slate-400">
          選択肢がありません
        </div>
      ) : null}
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(body, document.body);
}

/**
 * Multi-select sibling of {@link InlineChoice} for the 領域 cell (now a
 * multi-value array). Toggling an option calls `onCommit` with the full
 * next array — the popover stays open so the user can pick several. Closes
 * on outside click / Escape (which fire `onCancel`). Same portal anchoring
 * and viewport-flip logic as `InlineChoice`.
 */
export function InlineMultiChoice({
  options,
  value,
  anchorRect,
  onCommit,
  onCancel,
}: {
  options: readonly ChoiceOption[];
  value: readonly string[];
  anchorRect: DOMRect;
  onCommit: (next: string[]) => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    top: anchorRect.bottom + 2,
    left: anchorRect.left,
    minWidth: Math.max(160, anchorRect.width),
    zIndex: 60,
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.right > window.innerWidth - 4) {
      setStyle((s) => ({
        ...s,
        left: Math.max(4, anchorRect.right - rect.width),
      }));
    }
    if (rect.bottom > window.innerHeight - 4) {
      setStyle((s) => ({
        ...s,
        top: Math.max(4, anchorRect.top - rect.height - 2),
      }));
    }
  }, [anchorRect]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onCancel();
    };
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onClick);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onCancel]);

  const toggle = (opt: string): void => {
    const next = value.includes(opt)
      ? value.filter((v) => v !== opt)
      : [...value, opt];
    onCommit(next);
  };

  const body = (
    <div
      ref={ref}
      style={style}
      onClick={(e) => e.stopPropagation()}
      className="max-h-72 overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
      role="listbox"
      aria-multiselectable
    >
      {options.map((opt) => {
        const selected = value.includes(opt.value);
        return (
          <button
            key={opt.value || '__empty'}
            type="button"
            onClick={() => toggle(opt.value)}
            className={cn(
              'flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-slate-50',
              selected ? 'bg-slate-100' : '',
            )}
            role="option"
            aria-selected={selected}
          >
            <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
              {selected ? <Check className="h-3 w-3 text-slate-700" /> : null}
            </span>
            <span
              className={cn(
                'inline-block min-w-0 flex-1 truncate rounded px-1.5 py-0.5 text-[11px] font-medium',
                opt.badgeClasses,
              )}
              title={opt.label}
            >
              {opt.label}
            </span>
          </button>
        );
      })}
      {options.length === 0 ? (
        <div className="px-2 py-2 text-center text-xs text-slate-400">
          選択肢がありません
        </div>
      ) : null}
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(body, document.body);
}
